import { AUDIT_RETENTION_HELPER } from "@shared/types/auditIntegrity";
import { PLATFORM_SETTINGS, type PlatformSettingKey } from "@shared/types/settings";
import { GROUP_MAPPING_FLAG } from "@shared/types/groupAccess";
import { MONITOR_FLAG } from "@shared/types/monitor";
import { MCP_FLAG } from "@shared/mcp/constants";
import { PREVIEW_SETTINGS_FLAG } from "@shared/deploymentPolicy";
import { ACCOUNT_CREATION_FLAG } from "@shared/types/accountCreation";

/**
 * What each platform setting means to a reader, and the bounds the server will
 * enforce (FR-72, canvas 14 panel 01).
 *
 * The catalog itself — which keys exist, what shape each value takes, what a
 * reader falls back to — lives in `shared/types/settings.ts` and is CLOSED: a
 * key outside it is a 400 naming the keys that exist. What lives here is only
 * the presentation: the unit, the bounds repeated for the input's own
 * validation, and the sentence that says what changing it does.
 *
 * The bounds are repeated rather than read out of the zod schema because
 * reaching into a schema's internals to recover its constraints is a private
 * API by another name. Repeating them is safe because
 * `platform-settings.catalog.test.ts` asserts every non-secret key in the
 * shared catalog has an entry here — a setting added there and forgotten here
 * fails the suite rather than rendering as a bare number box.
 */

/**
 * The flag that shows the controls which are stored but not wired yet (product gaps row 20).
 *
 * The server accepts and stores every key in the closed catalog either way; this only decides
 * what the screen renders. Wiring one up later is its reader plus dropping its `preview` mark.
 * The name lives in `shared/deploymentPolicy.ts`, since a deployment policy may fix it (#2501).
 */
export { PREVIEW_SETTINGS_FLAG };

export interface NumericSettingDescriptor {
  key: PlatformSettingKey;
  unit: string;
  min: number;
  max: number;
  /** What changing it does, in the reader's terms. */
  help: string;
  /** Shown when the value is below `min`, because the floor has a reason. */
  floorReason?: string;
  /** Stored but read by nothing yet, so rendered only with `settings.preview` on. */
  preview?: boolean;
}

export const NUMERIC_SETTINGS: NumericSettingDescriptor[] = [
  {
    key: "audit.retention_days",
    unit: "days",
    min: 30,
    max: 3650,
    // Wired (HIPAA audit controls, 2026-09-29): the runner's retention reads it daily, so it is
    // no longer a preview-only control.
    help: AUDIT_RETENTION_HELPER,
    floorReason:
      "The audit trail is kept for at least 30 days in the database before a month can move to the immutable export.",
  },
  {
    key: "auth.reauth_max_age_seconds",
    unit: "seconds",
    min: 0,
    max: 3600,
    help: "0, the default, is off: sign-in is one click everywhere. 60 to 3600 asks for a sign-in that recent before sensitive changes such as a destination change, granting or revoking access, alert routing, an AI app approval, disabling a tenant, an instance or an AI app, connector access or an audit export.",
  },
  {
    key: "auth.mfa_remember_days",
    unit: "days",
    min: 0,
    max: 30,
    help: "How long a browser is remembered after a second sign-in step, when the person asks. 0 turns remembering off.",
  },
  {
    key: "auth.admin_session_max_hours",
    unit: "hours",
    min: 1,
    max: 12,
    help: "How long an admin's sign-in lasts while multi-factor sign-in covers admins, before they sign in again. At most 12 (NIST SP 800-63B).",
  },
  {
    key: "alerts.scan_stale_seconds",
    unit: "seconds",
    min: 60,
    max: 86_400,
    help: "How far behind the due scan may fall before the Health tab and the Log Analytics alert call it stale.",
  },
  {
    key: "runs.max_concurrent_per_instance",
    unit: "runs",
    min: 1,
    max: 100,
    help: "How many runs one instance may have in flight. A run over the limit waits rather than failing.",
    // Round 9 (B-26): offered, saved and audited, but read by nothing: the runner applies only the
    // instance's own limit (Settings > General) and the connector's (`runner/scheduler/capacity.ts`).
    // Hidden until a deployment-wide ceiling is wanted; wiring it is `loadCapacityLimits` taking
    // min(this, the instance's limit) and dropping this mark.
    preview: true,
  },
];

/**
 * Feature flags, with the sentence each one needs (FR-72).
 *
 * `features.flags` is a flat map so a new flag needs no migration, which also
 * means the stored document can carry a key this list has never heard of. Those
 * are rendered too, with no description, rather than hidden: a flag that is on
 * and invisible is the worst of the three states.
 *
 * #2501: every flag here is one some code reads (`settingsCatalog.flagsRead.test.ts`). The
 * retired ones (`RETIRED_FEATURE_FLAGS` in `shared/types/settings.ts`) are gone, and a flag the
 * deployment policy fixes gets no switch at all.
 */
export interface FeatureFlagDescriptor {
  key: string;
  label: string;
  description: string;
  /** Stored but read by nothing yet, so rendered only with `settings.preview` on. */
  preview?: boolean;
}

export const KNOWN_FEATURE_FLAGS: FeatureFlagDescriptor[] = [
  {
    // Wired (group access, 2026-09-28): the server reads it on every sign-in, so it is no
    // longer a preview-only control. Hidden by the owner on 2026-10-01: off everywhere, and
    // docs/research/2026-10-01-parked-group-access.md says how to turn it back on.
    key: GROUP_MAPPING_FLAG,
    label: "Entra group mapping",
    description:
      "Off, the default: access is by direct grants only and no screen shows group access. On: people in a mapped Entra group sign in with its role, and the Users screen gets a Directory groups tab.",
  },
  {
    // #1639 C1: read by the grant routes and the runner's account loop (`runner/accounts/`).
    key: ACCOUNT_CREATION_FLAG,
    label: "Email sign-in accounts",
    description:
      "Creates the Email sign-in account when an admin grants access to a person who has not signed in yet, then sends the invitation. Off: a Skypoint operator creates the account first.",
  },
  {
    // skyMonitor (spec section 11): off hides its page, the Overview tile and the Data
    // health rules, and idles the skyMonitor app. The dev runbook turns it on from here.
    key: MONITOR_FLAG,
    label: "skyMonitor · Data health",
    description:
      "Data health monitors on loaded tables: the skyMonitor page, the Overview tile and Data health notification rules.",
  },
  {
    // ADR 0009 (round 8): the platform kill switch for MCP. Off, the default everywhere and on a
    // dedicated deployment until the customer asks in writing: /mcp and the OAuth endpoints
    // answer 404. Tenant and instance switches narrow it further. /mcp reads it (`router.ts`), so
    // it is a normal setting, not a preview one.
    key: MCP_FLAG,
    label: "AI apps (MCP)",
    description:
      "Lets people connect Claude or ChatGPT to one instance and read its metadata, never customer data or credentials. Off: the MCP endpoints answer 404.",
  },
  {
    key: PREVIEW_SETTINGS_FLAG,
    label: "Preview settings",
    description: "settings.preview shows controls that are not wired yet.",
  },
];

/** The label the closed catalog gives a key, for a heading. */
export function settingLabel(key: PlatformSettingKey): string {
  return PLATFORM_SETTINGS[key].label;
}

/** The value a reader falls back to when the row has never been written. */
export function settingFallback(key: PlatformSettingKey): unknown {
  const spec = PLATFORM_SETTINGS[key] as { fallback?: unknown };
  return spec.fallback;
}
